import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { usePrivy, useLinkAccount, useUpdateAccount } from "@privy-io/react-auth";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { refreshPartnerSession } from "../../hooks/usePrivyAuth";

/**
 * Email attached to the partner's Privy login (display + link / change / remove).
 * Privy holds ONE email per identity and refuses to unlink the LAST login method,
 * so changing goes through Privy's own updateEmail flow (never unlink-then-link),
 * and removing is only offered while another login method (e.g. a wallet) exists.
 * After a change we re-run privyLogin so ppapi syncs the new email into the
 * partner row and the fresh session shows it in the header right away.
 */
const LoginEmailSection = () => {
  const dispatch = useDispatch();
  const { ready, authenticated, user, getAccessToken, unlinkEmail, unlinkGoogle } = usePrivy();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");

  const clearNotices = () => {
    setErr("");
    setMsg("");
  };

  const onFlowError = (code, details) => {
    setBusy(false);
    if (code === "exited_link_flow" || code === "exited_update_flow") return;
    if (code === "linked_to_another_user") {
      setErr(
        "That email is already attached to a different login. Sign in with that email instead, or use another address."
      );
      return;
    }
    const method = details?.linkMethod ? ` (${details.linkMethod})` : "";
    setErr(`Could not complete${method}: ${code || "unknown error"}`);
  };

  // Re-resolve the partner session so the dashboard reflects the new email immediately.
  const done = async (what) => {
    setErr("");
    try {
      const r = await refreshPartnerSession(dispatch, getAccessToken, user?.id || null);
      setMsg(r?.ok ? `${what}.` : `${what}. Sign out and back in to see it on your account.`);
    } catch (_) {
      setMsg(`${what}. Sign out and back in to see it on your account.`);
    } finally {
      setBusy(false);
    }
  };

  const { linkEmail, linkGoogle } = useLinkAccount({
    onSuccess: (_user, method) =>
      done(method === "google" ? "Google account linked" : "Email linked"),
    onError: onFlowError,
  });

  const { updateEmail } = useUpdateAccount({
    onSuccess: () => done("Email updated"),
    onError: onFlowError,
  });

  const emailAddr = user?.email?.address || "";
  const google = user?.google || null;
  const current = emailAddr || google?.email || "";

  // Login methods Privy would still accept after the email is gone. The auto-created
  // embedded wallet (walletClientType 'privy') is not one: nobody can sign in with it.
  const otherMethods = (user?.linkedAccounts || []).filter((a) => {
    if (!a || a.type === "email" || a.type === "google_oauth" || a.type === "smart_wallet") return false;
    if (a.type === "wallet") return a.walletClientType !== "privy";
    return true;
  }).length;
  const canRemove = Boolean(current) && otherMethods > 0;

  const startLink = (viaGoogle) => {
    clearNotices();
    setBusy(true);
    if (viaGoogle) linkGoogle();
    else linkEmail();
  };

  const change = () => {
    clearNotices();
    setBusy(true);
    // Privy's update flow swaps the address in place (verification included).
    if (emailAddr) return updateEmail();
    // Google-only identity: add the email first; Google can be removed afterwards.
    linkEmail();
  };

  const remove = async () => {
    if (!canRemove) return;
    const ok = window.confirm(
      `Remove ${current} from your login?\n\n` +
        `You will still be able to sign in with your wallet.`
    );
    if (!ok) return;
    clearNotices();
    setBusy(true);
    try {
      if (emailAddr) await unlinkEmail(emailAddr);
      if (google?.subject) await unlinkGoogle(google.subject);
      await done("Email removed from your login");
    } catch (e) {
      setBusy(false);
      setErr(e?.message || "Could not remove the email.");
    }
  };

  if (!ready || !authenticated) return null;

  return (
    <div className="panel" style={{ maxWidth: 900, marginBottom: 24, padding: 18 }}>
      <h3
        className="secH"
        style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 10 }}
      >
        <FontAwesomeIcon icon={faEnvelope} /> Login email
      </h3>
      <p className="secLead" style={{ marginTop: 0 }}>
        The email attached to your login. Once linked you can sign in with either
        your wallet or this email and land on the same partner account.
      </p>

      {current ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label className="fldLbl">Current login email</label>
            <div className="lwAddrBox" style={{ margin: 0, wordBreak: "break-all" }}>
              {current}
              {google && !emailAddr ? " (Google)" : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btnPrimary"
              onClick={change}
              disabled={busy}
            >
              {busy ? <FontAwesomeIcon icon={faSpinner} spin /> : "Change email"}
            </button>
            {!google && (
              <button
                type="button"
                className="btnGhost"
                onClick={() => startLink(true)}
                disabled={busy}
              >
                Add Google
              </button>
            )}
            {canRemove && (
              <button
                type="button"
                className="btnGhost"
                onClick={remove}
                disabled={busy}
              >
                Remove email
              </button>
            )}
          </div>
          {!canRemove && (
            <div className="fldHelp">
              This email is your only sign-in method, so it cannot be removed. Link a
              wallet first if you want to remove it.
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div className="notice">
            <span className="ni">i</span>
            <div>No email is attached to your login yet. Link one to also sign in by email.</div>
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btnPrimary"
              onClick={() => startLink(false)}
              disabled={busy}
            >
              {busy ? <FontAwesomeIcon icon={faSpinner} spin /> : "Link an email"}
            </button>
            <button
              type="button"
              className="btnGhost"
              onClick={() => startLink(true)}
              disabled={busy}
            >
              Link Google
            </button>
          </div>
        </div>
      )}

      {err && (
        <div className="notice" style={{ marginTop: 14 }}>
          <span className="ni">!</span>
          <div>{err}</div>
        </div>
      )}
      {msg && (
        <div className="notice green" style={{ marginTop: 14 }}>
          <span className="ni">✓</span>
          <div>{msg}</div>
        </div>
      )}
    </div>
  );
};

export default LoginEmailSection;
